<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import { watchDebounced } from '@vueuse/core'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import GIcon from '@consumer/components/GIcon.vue'
import GTag from '@consumer/components/GTag/GTag.vue'
import { canonicalSignalValue, conformSignalText } from './conformSignalText'
import { signalFilled, useConciergeState } from './conciergeState'
import { recommendationReadiness } from './recommendationRequest'
import { trackConciergeEvent } from './conciergeEventTracking'
import { useConciergeTakeover } from './useConciergeTakeover'
import { occasionPhrase, subjectPronoun } from './occasionPhrase'
import { seasonalOccasionOptions } from './seasonalOccasions'
import { seasonalRelationshipLabels } from './seasonalRelationships'
import {
  INLINE_OPTION_LIMIT,
  INTEREST_LIMIT,
  MORE_LABELS,
  OPEN_LABELS,
  QUESTION_HINTS,
  QUESTION_ORDER,
  RELATIONSHIP_OPTIONS,
  TAP_OPTIONS,
  type TapOption,
  type TapSignal,
  VISIBLE_RELATIONSHIP_COUNT,
} from './tapsOptions.data'

// Budget is deliberately not asked — it's only ever captured when a buyer
// volunteers one, and there's no way to volunteer here.

withDefaults(defineProps<{
  framed?: boolean
  referenceDate?: Date
}>(), { framed: false,referenceDate: () => (new Date()), })

const state = useConciergeState()
const takeover = useConciergeTakeover()

const occasionOptions = seasonalOccasionOptions(__props.referenceDate)

// The relationship list with the day's seasonal picks (Mom near Mother's Day,
// etc.) pulled to the very front, nearest day first — so a boosted one leads the
// visible set instead of hiding behind "Someone else…". A plain reorder of the
// same options: nothing is added or dropped.
const relationshipOptions = _computed(() => {
  const boosted = seasonalRelationshipLabels(__props.referenceDate)
  if (!boosted.length) return RELATIONSHIP_OPTIONS

  const front = boosted.flatMap(label => RELATIONSHIP_OPTIONS.filter(option => option.label === label))
  const rest = RELATIONSHIP_OPTIONS.filter(option => !boosted.includes(option.label))
  return [...front, ...rest]
})

const visibleRelationshipOptions = _computed(() =>
  relationshipOptions.value.slice(0, VISIBLE_RELATIONSHIP_COUNT))

const moreRelationshipOptions = _computed(() =>
  relationshipOptions.value.slice(VISIBLE_RELATIONSHIP_COUNT))

// Answered for the UI's purposes, not readiness: a custom entry no list resolves
// keeps `display` with no value (their words render as a chip, nothing reaches
// the request — same rule as the madlibs blanks), and re-asking a question the
// buyer just answered would read as arguing with them.
function answered (name: TapSignal): boolean {
  const signal = state.signals[name]
  return signalFilled(signal) || signal.display !== null
}

// A plain ref, not derived from the store: interests stay active while the buyer
// multi-selects, so "first unanswered" would advance out from under them on the
// first tap. Scalar taps advance explicitly; rows and chips reopen explicitly.
let activeQuestion = _ref<TapSignal | null>(QUESTION_ORDER.find(name => !answered(name)) ?? null)

const upcoming = _computed(() =>
  QUESTION_ORDER.filter(name => name !== activeQuestion.value && !answered(name)))

const complete = _computed(() => activeQuestion.value === null && !upcoming.value.length)

// Custom entries join the options they were typed under, so an added value shows
// up as a selected chip right where the input sat instead of vanishing into the
// brief above.
const customOptions = _ref<Record<TapSignal, TapOption[]>>({
  relationship: [],
  interests: [],
  location: [],
  occasion: [],
})

function optionsFor (name: TapSignal): TapOption[] {
  if (name === 'occasion') return [...occasionOptions, ...customOptions.value.occasion]
  if (name === 'relationship') return [...relationshipOptions.value, ...customOptions.value.relationship]

  return [...TAP_OPTIONS[name], ...customOptions.value[name]]
}

let moreRelationshipsShown = _ref(false)

function showMoreRelationships () {
  moreRelationshipsShown.value = true
}

// Reopening with a value that was picked from the expanded set keeps the whole
// list out, so the selected chip is never hidden behind "Someone else…".
const showAllRelationships = _computed(() =>
  moreRelationshipsShown.value || moreRelationshipOptions.value.some(option => isSelected('relationship', option)))

function activeOptionsFor (name: TapSignal): TapOption[] {
  if (soloCustomFor.value === name) return []
  if (name === 'relationship' && !showAllRelationships.value)
    return [...visibleRelationshipOptions.value, ...customOptions.value.relationship]

  return optionsFor(name)
}

function inlineOptionsFor (name: TapSignal): TapOption[] {
  return optionsFor(name).slice(0, INLINE_OPTION_LIMIT)
}

// Per-question copy read via a param, not `RECORD[activeQuestion]` directly: the
// $ref reactivity transform brands `activeQuestion` so it can't index a Record,
// but it passes fine to a plain TapSignal parameter (same reason `optionsFor`
// and `questionTitle` take a param). The loop-variable `name` isn't branded, so
// those call sites index inline.
function hintFor (name: TapSignal): string {
  return QUESTION_HINTS[name]
}

function openLabelFor (name: TapSignal): string {
  return OPEN_LABELS[name]
}

const TONES: Record<TapSignal, { accent: string, surface: string }> = {
  relationship: { accent: 'var(--red-400)', surface: 'var(--color-bg-surface-red)' },
  interests: { accent: 'var(--rive-accent-foreground)', surface: 'var(--color-bg-surface-pink)' },
  location: { accent: 'var(--blue-400)', surface: 'var(--color-bg-surface-blue)' },
  occasion: { accent: 'var(--yellow-500)', surface: 'var(--color-bg-surface-yellow)' },
}

function toneStyle (name: TapSignal) {
  const tone = TONES[name]
  return { '--tap-accent': tone.accent, '--tap-surface': tone.surface }
}

function advanceFrom (name: TapSignal) {
  activeQuestion.value = QUESTION_ORDER.find(question => question !== name && !answered(question)) ?? null
}

function questionTitle (name: TapSignal): string {
  const pronoun = subjectPronoun(state.signals.relationship)
  switch (name) {
    case 'relationship': return 'Who are you shopping for?'
    case 'interests': return pronoun ? `What's ${pronoun} into?` : 'What are they into?'
    case 'location': return pronoun ? `Where does ${pronoun} live?` : 'Where do they live?'
    case 'occasion': return "What's the occasion?"
  }
}

function isSelected (name: TapSignal, option: TapOption): boolean {
  const signal = state.signals[name]
  if (Array.isArray(signal.value)) return option.value !== null && signal.value.includes(option.value)

  // The display must match too: options can share a canonical (Wife and Husband
  // are both `spouse`), and display-only customs all carry a null value.
  return signal.value === option.value && (signal.display ?? null) === (option.display ?? null)
}

function applyOption (name: TapSignal, option: TapOption) {
  if (name === 'interests') {
    if (option.value === null) return
    const current = state.signals.interests.value
    if (!current.includes(option.value) && current.length >= INTEREST_LIMIT) return
    const values = current.includes(option.value)
      ? current.filter(value => value !== option.value)
      : [...current, option.value]
    if (values.length) state.setSignal('interests', values)
    else state.clearSignal('interests')
    return
  }

  if (isSelected(name, option)) {
    state.clearSignal(name)
    return
  }

  state.setSignal(name, option.value, { display: option.display ?? null })
}

function tapOption (name: TapSignal, option: TapOption) {
  closeCustom()
  applyOption(name, option)
  // Advancing unmounts the tapped chip, so move focus onto the newly opened
  // question's first option — otherwise focus falls back to <body> and a
  // keyboard/screen-reader user is stranded.
  if (name === 'interests') {
    if (state.signals.interests.value.length >= INTEREST_LIMIT) {
      advanceFrom('interests')
      focusActiveOption()
    }
    return
  }
  if (answered(name)) {
    advanceFrom(name)
    focusActiveOption()
  }
}

// An inline tap on a collapsed row answers that question in place — it must not
// pull focus off the question the buyer is actually on.
function tapInline (name: TapSignal, option: TapOption) {
  applyOption(name, option)
}

function openQuestion (name: TapSignal) {
  if (activeQuestion.value === name) return
  activeQuestion.value = name
  focusActiveOption()
}

// "more…" says none of the visible options fit, so the question opens ready for
// the long tail: the full list for relationships, the free-text box — already
// focused — everywhere else. For locations the curated cities are examples, not
// a vocabulary, so the box opens alone; leaving it brings the chips back, and
// every other door into the question keeps them.
let soloCustomFor = _ref<TapSignal | null>(null)

function openQuestionForMore (name: TapSignal) {
  activeQuestion.value = name
  if (name === 'relationship') {
    showMoreRelationships()
    focusActiveOption()
    return
  }
  if (name === 'location') soloCustomFor.value = name
  openCustom(name)
}

const optionsEl = ref<HTMLElement>()
const summaryEl = ref<HTMLElement>()

// With no question open the brief is complete and the read-back sentence is all that is left
// to land on; focusing it is also what gets it announced.
function focusActiveOption () {
  nextTick(() => (optionsEl.value?.querySelector('button') ?? summaryEl.value)?.focus())
}

// The escape hatch: a small free-text entry that runs through the same matching
// helpers as the madlibs blanks — spelling fixed on the way out, closed
// vocabularies (relationship, occasion) send nothing when unmatched, open ones
// (location, interests) carry the long-tail value whole.
let customFor = _ref<TapSignal | null>(null)
let customText = _ref('')
const customInput = ref<HTMLInputElement>()

function openCustom (name: TapSignal) {
  customFor.value = name
  customText.value = ''
  nextTick(() => customInput.value?.focus())
}

function closeCustom () {
  customFor.value = null
  customText.value = ''
  soloCustomFor.value = null
}

// `refocus` only on the Enter path: refocusing on blur would yank focus back to
// the input when the buyer clicks away mid-thought.
function commitCustom (refocus = false) {
  const name = customFor.value
  if (!name) return

  const typed = customText.value.trim()
  if (!typed) {
    closeCustom()
    return
  }

  const shown = conformSignalText(name, typed) ?? typed
  const value = canonicalSignalValue(name, shown)
  rememberCustomOption(name, value, shown)

  if (name === 'interests') {
    const values = [...new Set([...state.signals.interests.value, value ?? shown])].slice(0, INTEREST_LIMIT)
    state.setSignal('interests', values)

    if (values.length >= INTEREST_LIMIT) {
      closeCustom()
      advanceFrom('interests')
      focusActiveOption()
      return
    }

    // The input stays open and empty — the new chip appears where it sat, and
    // the box shifts right, ready for the next one.
    customText.value = ''
    if (refocus) nextTick(() => customInput.value?.focus())
    return
  }

  state.setSignal(name, value, { display: shown })
  advanceFrom(name)
  closeCustom()
  // Enter/Tab unmount the box the buyer is in, so hand focus to what comes next (the opened
  // question, or the read-back sentence when this was the last answer); a blur commit leaves
  // focus where they clicked.
  if (refocus) focusActiveOption()
}

function rememberCustomOption (name: TapSignal, value: string | null, shown: string) {
  const duplicate = optionsFor(name).some(option =>
    (option.value !== null && option.value === value) || option.label.toLowerCase() === shown.toLowerCase())
  if (duplicate) return

  customOptions.value[name].push({ label: shown, value, display: shown })
}

function customLabel (name: TapSignal): string {
  return OPEN_LABELS[name].replace('…', '')
}

// Tab commits like Enter instead of tabbing away mid-entry; an empty box lets
// Tab pass through so the keyboard can still leave the input.
function onCustomTab (event: KeyboardEvent) {
  if (!customText.value.trim()) return

  event.preventDefault()
  commitCustom(true)
}

type BriefChip = { key: string, label: string, emoji: string, remove: () => void }

// TEMPORARY (concept-proving): interests use one placeholder glyph, same as the
// free-text chips. In production each interest carries its own GiftTag icon_url.
const INTEREST_GLYPH = '✨'

const brief = _computed<BriefChip[]>(() => {
  const { relationship, occasion, location, interests } = state.signals
  const chips: BriefChip[] = []
  const scalars = [
    { name: 'relationship' as const, signal: relationship, emoji: '🧑' },
    { name: 'occasion' as const, signal: occasion, emoji: '🎉' },
    { name: 'location' as const, signal: location, emoji: '📍' },
  ]
  for (const { name, signal, emoji } of scalars) {
    const label = signal.display ?? signal.value
    if (label) chips.push({ key: name, label, emoji, remove: () => reopenQuestion(name) })
  }
  for (const interest of interests.value)
    chips.push({ key: `i-${interest}`, label: interest, emoji: INTEREST_GLYPH, remove: () => removeInterest(interest) })
  return chips
})

// Removing a chip unmounts the button that was clicked, so the reopened question takes focus.
function reopenQuestion (name: TapSignal) {
  state.clearSignal(name)
  activeQuestion.value = name
  focusActiveOption()
}

function removeInterest (value: string) {
  const values = state.signals.interests.value.filter(entry => entry !== value)
  if (values.length) state.setSignal('interests', values)
  else state.clearSignal('interests')
  activeQuestion.value = 'interests'
  focusActiveOption()
}

// Deliberately a private copy of the madlibs synthesizer, not a shared helper:
// each variant owns its sentence shape so one can rephrase (A/B copy tests)
// without conflating the others. Phrased in words the parser recognises, since
// the persisted sentence seeds — and is re-parsed by — the free-text input on a
// later page.
function synthesizeQuery (): string {
  const { relationship, location, interests, occasion } = state.signals
  const clauses = [`Find something for my ${relationship.display ?? relationship.value ?? 'someone'}`]
  if (location.value) clauses.push(`in ${location.display ?? location.value}`)
  if (interests.value.length)
    clauses.push(`who's really into ${interests.display ?? interests.value.join(' and ')}`)

  const sentence = clauses.join(' ')
  const reason = occasionPhrase(occasion, relationship)
  return reason ? `${sentence} — it's for ${reason}.` : `${sentence}.`
}

// Curly quotes mark the sentence as the brief read back, not another question.
const summary = _computed(() => `“${synthesizeQuery()}”`)

const readiness = _computed(() => recommendationReadiness(state.signals))

// Engagement latches on a deliberate control activation (a chip or the custom entry) and
// releases when the takeover collapses: persisted signals must not open it on load, and a
// dismiss holds until the buyer taps back in. A backdrop click always dismisses, and emptying
// the brief closes it — there is no field to retype into. The first activation is also the
// funnel's first_interaction, once per mount, as the first keystroke is in the other inputs.
let activated = _ref(false)
let interacted = false
function onActivate (event: MouseEvent) {
  if (!(event.target instanceof Element) || !event.target.closest('button, input')) return
  activated.value = true
  if (interacted) return
  interacted = true
  trackConciergeEvent('first_interaction', { variant: 'taps' })
}
watch(() => takeover?.engaged.value, (open) => {
  if (!open) activated.value = false
})
const stopReporting = takeover?.registerInput({
  active: () => activated.value,
  empty: () => readiness.value.filledCount === 0,
  dismissOnOutsideClick: () => true,
  collapseOnEmpty: () => true,
})
onUnmounted(() => stopReporting?.())

// The results surface fetches off the store's signals, but gates that fetch on
// state.raw being non-empty — so, like the madlibs input, this variant populates
// raw with the synthesized sentence once there's enough to search, and clears it
// back to '' when readiness drops. Debounced so it settles when the buyer stops.
watchDebounced(
  () => state.signals,
  () => {
    state.raw = readiness.value.ready ? synthesizeQuery() : ''
  },
  { debounce: 400, deep: true, immediate: true },
)
</script>

<template>
  <div class="concierge-taps" @click.capture="onActivate">
    <div class="panel" :class="{ framed }">
      <TransitionGroup
        v-if="brief.length"
        tag="ul"
        name="chip"
        class="brief"
        aria-label="Your details so far"
      >
        <li v-for="chip in brief" :key="chip.key">
          <GTag
            size="xs"
            variant="unselected"
            :emoji="chip.emoji"
            :label="chip.label"
            removable
            @remove="chip.remove()"
          />
        </li>
      </TransitionGroup>

      <Transition name="question">
        <div v-if="activeQuestion" :key="activeQuestion" class="question" :style="toneStyle(activeQuestion)">
          <div class="question-head">
            <span
              class="question-title"
              :data-title="questionTitle(activeQuestion)"
            >{{ questionTitle(activeQuestion) }}</span>
            <span v-if="soloCustomFor !== activeQuestion" class="question-hint">{{ hintFor(activeQuestion) }}</span>
          </div>
          <div ref="optionsEl" class="options">
            <button
              v-for="option in activeOptionsFor(activeQuestion)"
              :key="option.label"
              type="button"
              class="tap-chip"
              :class="{ selected: isSelected(activeQuestion, option) }"
              :aria-pressed="isSelected(activeQuestion, option)"
              @click="tapOption(activeQuestion, option)"
            >
              {{ option.label }}
              <GIcon v-if="isSelected(activeQuestion, option)" :icon="faCheck" size="xs"/>
            </button>
            <button
              v-if="activeQuestion === 'relationship' && !showAllRelationships"
              type="button"
              class="tap-chip open"
              @click="showMoreRelationships"
            >
              {{ openLabelFor(activeQuestion) }}
            </button>
            <input
              v-else-if="activeQuestion !== 'relationship' && customFor === activeQuestion"
              ref="customInput"
              v-model="customText"
              type="text"
              class="custom-input"
              autocomplete="off"
              :aria-label="customLabel(activeQuestion)"
              @keydown.enter="commitCustom(true)"
              @keydown.tab="onCustomTab"
              @keydown.esc.stop="closeCustom"
              @blur="commitCustom(false)"
            >
            <button
              v-else-if="activeQuestion !== 'relationship'"
              type="button"
              class="tap-chip open"
              @click="openCustom(activeQuestion)"
            >
              {{ openLabelFor(activeQuestion) }}
            </button>
          </div>
        </div>
        <p
          v-else-if="complete"
          ref="summaryEl"
          key="summary"
          class="summary"
          tabindex="-1"
          :data-title="summary"
        >
          {{ summary }}
        </p>
      </Transition>

      <TransitionGroup v-if="upcoming.length" tag="div" name="row" class="upcoming">
        <div
          v-for="name in upcoming"
          :key="name"
          class="upcoming-row"
          :style="toneStyle(name)"
        >
          <button
            type="button"
            class="upcoming-title"
            @click="openQuestion(name)"
          >
            {{ questionTitle(name) }}
          </button>
          <button
            v-for="option in inlineOptionsFor(name)"
            :key="option.label"
            type="button"
            class="mini-chip"
            :class="{ selected: isSelected(name, option) }"
            :aria-pressed="isSelected(name, option)"
            @click="tapInline(name, option)"
          >
            {{ option.label }}
          </button>
          <button
            v-if="optionsFor(name).length > INLINE_OPTION_LIMIT"
            type="button"
            class="mini-chip open"
            :aria-label="MORE_LABELS[name]"
            @click="openQuestionForMore(name)"
          >
            more…
          </button>
        </div>
      </TransitionGroup>

      <p v-if="!readiness.ready" class="request-hint">{{ readiness.reason }}</p>
      <p v-else-if="complete" class="summary-hint">Remove a detail to change it.</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "./conciergeRainbow" as rainbow;

// Caps its own column like the other concierge inputs: the takeover surface
// spans the page's content width, so the input centers at a readable width.
.concierge-taps {
  display: grid;
  gap: 8px;
  margin: 0 auto;
  max-width: 720px;
}

.panel {
  display: grid;
  gap: 12px;
}

// The framed madlibs panel's surface — see ConciergeMadlibs for why the rainbow
// is painted as a real border rather than a strip.
.panel.framed {
  animation: concierge-rainbow-pan 8s linear infinite;
  background:
    linear-gradient(var(--color-bg-surface-white), var(--color-bg-surface-white)) padding-box,
    rainbow.$gradient border-box;
  background-size: auto, 200% 100%;
  border: 0 solid transparent;
  border-bottom-width: 3px;
  border-radius: 20px;
  box-shadow: 0 1px 3px rgb(0 0 0 / 8%);
  padding: 24px 20px;
}

@media (prefers-reduced-motion: reduce) {
  .panel.framed {
    animation: none;
  }
}

.brief {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.chip-enter-active,
.chip-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.chip-enter-from,
.chip-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

.question {
  display: grid;
  gap: 8px;
}

// The focused question expands in as the previous one clears. Enter-only on
// purpose: the outgoing question leaves at once (no lingering copy to overlap
// the new one, and no delay before the auto-focus lands on the new options).
.question-enter-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.question-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

// An answered question's row drops out at once (no lingering copy overlapping
// the question it just became); the survivors then glide up into its place, and
// a reopened row eases back in.
.row-move,
.row-enter-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.row-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .chip-enter-active,
  .chip-leave-active,
  .question-enter-active,
  .row-move,
  .row-enter-active {
    transition: none;
  }
}

.question-head {
  align-items: baseline;
  display: flex;
  justify-content: space-between;
}

// The focused section's title flags itself alive. The title text itself stays
// solid navy at all times — the rainbow rides on a duplicate copy layered on
// top (::after, via the data-title attribute) that is masked down to a single
// travelling band, so the letters can never wash out to invisible the way a
// clip-the-text-with-a-gradient approach does. The band sweeps once as the
// section expands (the title remounts on expand, restarting the animation),
// then again every few seconds; at rest the mask hides the copy entirely and
// only the navy underneath shows.
.question-title {
  color: var(--color-text-primary-dark);
  font-size: 0.9375rem;
  font-weight: 600;
  position: relative;
}

// The band rides a duplicate copy of the text (::after, read from data-title)
// on both the question title and the read-back sentence; only the animation
// differs. Transparent fill so only the clipped gradient shows; the `/ ""`
// gives the copy empty alt text so assistive tech ignores it (otherwise the
// text is announced twice).
.question-title::after,
.summary::after {
  background-clip: text;
  background-image: rainbow.$sweep-gradient;
  color: transparent;
  content: attr(data-title) / "";
  inset: 0;
  mask-image: linear-gradient(90deg, transparent 0%, #000 35%, #000 65%, transparent 100%);
  mask-repeat: no-repeat;
  mask-size: 260% 100%;
  position: absolute;
}

// A half-second beat before the first sweep so a freshly opened section
// settles before the rainbow arrives (the delay applies once, on the initial
// run after expand; repeats keep the 4.5s cadence). `backwards` holds the
// band off-screen through that beat so the title stays clean navy, not a
// flash of colour.
.question-title::after {
  animation: concierge-title-sweep 4.5s ease-in-out 0.5s infinite backwards;
}

// The sentence flashes once as it appears — a finished brief is a moment, not
// a section awaiting input — so no repeats. `both` keeps the delay beat clean
// and then holds the copy at the keyframes' final opacity 0; without the
// forwards half a finished one-shot snaps back to a fully coloured overlay.
.summary::after {
  animation: concierge-title-sweep 4.5s ease-in-out 0.5s 1 both;
}

// Sweep right-to-left in ~0.7s (0 → 16% of the 4.5s cycle) — matching the way
// the panel's bottom rainbow travels — then fade the copy out entirely so no
// sliver of colour clings to the letters and only the navy title shows at rest.
@keyframes concierge-title-sweep {
  0% {
    mask-position: -60% 0;
    opacity: 1;
  }

  16% {
    mask-position: 130% 0;
    opacity: 1;
  }

  22% {
    mask-position: 130% 0;
    opacity: 0;
  }

  100% {
    mask-position: 130% 0;
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .question-title::after,
  .summary::after {
    animation: none;
    content: none;
  }
}

.question-hint {
  color: var(--color-text-tertiary);
  font-size: 0.75rem;
  font-weight: 600;
}

.options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tap-chip {
  align-items: center;
  background: var(--color-bg-surface-white);
  border: 1px solid var(--color-border-primary);
  border-radius: 999px;
  color: var(--color-text-secondary-dark);
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 6px;
  line-height: 1.3;
  padding: 9px 14px;

  &.selected {
    background: var(--tap-surface);
    border-color: var(--tap-accent);
    color: var(--tap-accent);
    padding: 9px 15px;
  }

  &.open {
    border-style: dashed;
    color: var(--color-text-secondary-dark);
  }
}

.custom-input {
  border: 1px dashed var(--tap-accent);
  border-radius: 999px;
  color: var(--color-text-primary-dark);
  font: inherit;
  font-size: 0.9375rem;
  line-height: 1.35;
  padding: 12px 16px;
  width: 190px;

  // Swap the UA outline for a tone-matched ring so keyboard focus stays visible
  // (WCAG 2.4.7) — a bare `outline: none` would leave no focus indicator.
  &:focus-visible {
    border-style: solid;
    box-shadow: 0 0 0 2px var(--tap-accent);
    outline: none;
  }
}

.upcoming {
  display: grid;
  gap: 8px;
}

// One line, never wrapping: the collapsed rows are what made the module tall.
// Overflow is clipped rather than wrapped — INLINE_OPTION_LIMIT is tuned so the
// title, its few example chips and "more…" fit the 620px column.
.upcoming-row {
  align-items: center;
  background: var(--color-bg-surface-grey);
  border-radius: 12px;
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  overflow: hidden;
  padding: 6px 10px;
}

.upcoming-title {
  background: none;
  border: 0;
  color: var(--color-text-tertiary);
  cursor: pointer;
  flex-shrink: 0;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  padding: 10px 6px;
  text-align: left;
  white-space: nowrap;
}

.mini-chip {
  align-items: center;
  background: var(--color-bg-surface-white);
  border: 1px solid var(--color-border-primary);
  border-radius: 999px;
  color: var(--color-text-secondary-dark);
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
  padding: 7px 10px;
  white-space: nowrap;

  &.selected {
    background: var(--tap-surface);
    border-color: var(--tap-accent);
    color: var(--tap-accent);
  }

  &.open {
    border-style: dashed;
  }
}

.request-hint {
  color: var(--color-text-tertiary);
  font-size: 0.8125rem;
  margin: 0;
}

.summary {
  color: var(--color-text-primary-dark);
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
  position: relative;

  // The completion focus target (see focusActiveOption): keyboard users get a ring in the
  // chips' style, a mouse-driven completion shows none.
  &:focus-visible {
    border-radius: 4px;
    box-shadow: 0 0 0 2px var(--color-border-primary);
    outline: none;
  }
}

.summary-hint {
  color: var(--color-text-tertiary);
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0;
}
</style>
